<template>
  <div class="invite-page">
    <div class="bg-decoration"></div>
    <div class="bg-decoration-2"></div>

    <div class="main-content">

      <!-- 未通过签名验证引导 -->
      <div v-if="!authVerified" class="no-access">
        <div v-if="authVerifying" class="no-access-card">
          <i class="el-icon-loading no-access-icon"></i>
          <p class="no-access-title">正在验证身份...</p>
        </div>
        <div v-else-if="authDeniedReason" class="no-access-card denied">
          <i class="el-icon-warning no-access-icon"></i>
          <p class="no-access-title">无法参与活动</p>
          <p class="no-access-desc">{{ authDeniedReason }}</p>
        </div>
        <div v-else class="no-access-card">
          <i class="el-icon-warning-outline no-access-icon"></i>
          <p class="no-access-title">请通过App内活动链接进入</p>
          <p class="no-access-desc">本活动页需在陌撩漂流瓶App内打开，方能识别您的身份并参与奖励活动。</p>
        </div>
      </div>

      <template v-else>
        <!-- 顶部返回 + 品牌 -->
        <div class="top-bar">
          <span class="back-btn" @click="goBack"><i class="el-icon-arrow-left"></i> 活动中心</span>
          <span class="brand-name">陌撩漂流瓶</span>
        </div>

        <!-- 主视觉 Hero -->
        <div class="hero-card">
          <div class="hero-badge">
            <span class="dot"></span>限时活动
          </div>
          <h1 class="hero-title">邀请好友</h1>
          <div class="hero-cashback">奖励 <span class="num">10</span><span class="pct">%</span></div>
          <p class="hero-desc">每邀请一位好友注册，享受好友提现金额 <b>10%</b> 奖励，多邀多得，上不封顶。</p>

          <div class="hero-stats">
            <div class="stat-item">
              <div class="stat-num">{{ inviteesTotal }}</div>
              <div class="stat-label">已邀请人数</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <div class="stat-num">10%</div>
              <div class="stat-label">奖励比例</div>
            </div>
          </div>
        </div>

        <!-- 标签页区域 -->
        <div class="tabs-card">
          <el-tabs v-model="activeTab" class="invite-tabs" @tab-click="onTabClick">
            <!-- 邀请信息标签页 -->
            <el-tab-pane label="邀请信息" name="invite">
              <!-- 邀请码绑定 -->
              <div class="bind-card">
                <div class="card-title">
                  <i class="el-icon-connection"></i>
                  <span>邀请码绑定</span>
                  <span v-if="bindingDetail" class="bind-status-tag">已绑定</span>
                </div>

                <div v-loading="bindingLoading" class="bind-body">
                  <!-- 已绑定：展示邀请人头像+详情 -->
                  <template v-if="bindingDetail">
                    <div class="bound-info">
                      <!-- 头像 -->
                      <div class="bound-avatar-wrap">
                        <img v-if="avatarUrl(bindingDetail.inviter_avatar)"
                             :src="avatarUrl(bindingDetail.inviter_avatar)"
                             class="bound-avatar-img"
                             alt=""
                             @error="onAvatarError" />
                        <div class="user-avatar bound-avatar-placeholder"
                             :style="avatarUrl(bindingDetail.inviter_avatar)
                               ? { display: 'none' }
                               : { background: avatarColor(bindingDetail.inviter_nickname) }">
                          {{ initial(bindingDetail.inviter_nickname) }}
                        </div>
                      </div>
                      <!-- 信息 -->
                      <div class="bound-detail">
                        <div class="bound-name">
                          {{ bindingDetail.inviter_nickname || '匿名用户' }}
                          <span class="bound-sex-tag" :class="bindingDetail.inviter_sex === 2 ? 'female' : 'male'">{{ sexText(bindingDetail.inviter_sex) }}</span>
                        </div>
                        <div class="bound-id">ID：{{ bindingDetail.inviter_user_id }}</div>
                        <div class="bound-time">绑定时间：{{ bindingDetail.created_at }}</div>
                      </div>
                    </div>
                    <p class="bind-tip">您已绑定邀请码，每位用户仅可绑定一次，无法重复绑定。</p>
                  </template>

                  <!-- 未绑定：展示绑定表单 -->
                  <template v-else>
                    <!-- 超过绑定期限提示 -->
                    <div v-if="!canBindInvite" class="bind-expired-notice">
                      <i class="el-icon-warning-outline"></i>
                      <span>您的注册时间已超过3天，无法进行邀请码绑定操作。</span>
                    </div>
                    <el-input
                      v-model="bindCode"
                      placeholder="请输入邀请人的数字邀请码"
                      clearable
                      maxlength="20"
                      :disabled="!canBindInvite || previewLoading || confirmLoading"
                      @keyup.enter.native="handleBind"
                    >
                      <i slot="prefix" class="el-input__icon el-icon-key"></i>
                    </el-input>

                    <el-button
                      type="primary"
                      class="submit-btn"
                      :loading="previewLoading"
                      :disabled="!canBindInvite || previewLoading || confirmLoading"
                      @click="handleBind"
                    >
                      立即绑定
                    </el-button>

                    <p class="bind-tip">绑定后将与邀请人建立奖励关系，请确认邀请码无误。仅女性用户可参与绑定。</p>
                  </template>
                </div>
              </div>

              <!-- 被邀请人列表 -->
              <div class="bind-card">
                <div class="card-section-title">
                  <i class="el-icon-user"></i>
                  <span>被邀请人{{ inviteesTotal ? ' (' + inviteesTotal + ')' : '' }}</span>
                  <span class="invitees-sort-wrap">
                    <el-radio-group v-model="inviteesSort" size="mini" @change="onInviteesSortChange">
                      <el-radio-button label="desc">最新</el-radio-button>
                      <el-radio-button label="asc">最早</el-radio-button>
                    </el-radio-group>
                  </span>
                </div>

                <!-- 复制邀请链接 -->
              <div class="copy-link-bar">
                <div class="link-preview" :title="inviteLink">{{ inviteLink }}</div>
                <el-button
                  :class="['copy-link-btn', { 'copy-success': copySuccess }]"
                  size="mini"
                  @click="copyInviteLink"
                >
                  <i :class="copySuccess ? 'el-icon-check' : 'el-icon-document-copy'"></i>
                  {{ copySuccess ? '已复制' : '复制链接' }}
                </el-button>
              </div>

              <div v-loading="inviteesLoading" class="list-wrap">
                  <div v-if="invitees.length" class="invitee-list">
                    <div v-for="item in invitees" :key="item.id" class="invitee-card" :class="{ 'invitee-blacklisted': item.is_blacklisted }">
                        <div class="invitee-head">
                          <div class="invitee-user">
                            <!-- 头像：有效URL时显示图片，加载失败回退到彩色首字母 -->
                            <img v-if="avatarUrl(item.avatar)" :src="avatarUrl(item.avatar)" class="user-avatar-img" alt="" @error="onAvatarError" />
                            <div class="user-avatar" :style="avatarUrl(item.avatar) ? { display: 'none' } : { background: avatarColor(item.nickname) }">{{ initial(item.nickname) }}</div>
                            <div class="invitee-meta">
                              <div class="invitee-name">
                                {{ item.nickname || '匿名用户' }}
                                <span v-if="item.is_blacklisted" class="blacklist-tag">黑名单</span>
                              </div>
                              <div class="invitee-id">ID：{{ item.user_id }}</div>
                            </div>
                          </div>
                          <div class="invitee-time">邀请时间：{{ item.created_at }}</div>
                        </div>
                      </div>
                  </div>

                  <el-empty v-else description="还未邀请任何好友" :image-size="80"></el-empty>
                </div>

                <div v-if="inviteesTotal > inviteesPageSize" class="pager">
                  <el-pagination
                    background
                    layout="prev, pager, next"
                    :current-page="inviteesPage"
                    :page-size="inviteesPageSize"
                    :total="inviteesTotal"
                    small
                    @current-change="onInviteesPageChange"
                  ></el-pagination>
                </div>
              </div>

              <!-- 活动规则 -->
              <div class="bind-card">
                <div class="card-section-title no-border">
                  <i class="el-icon-document-checked"></i>
                  <span>活动规则</span>
                </div>
                <ol class="rules-list">
                  <li>在「绑定邀请码」中输入邀请人的数字<b>ID</b>，完成绑定。</li>
                  <li>仅注册时间在<b>3天以内（含3天）</b>的新用户可进行邀请码绑定操作。</li>
                  <li>好友每笔提现，你均可获得提现金额 <b>10%</b> 的奖励。</li>
                  <li>奖励金额累计满 <b>2 元</b>（含）后可发起提现，款项将转入您绑定的提现账户。</li>
                </ol>
              </div>
            </el-tab-pane>

            <!-- 奖励记录标签页 -->
            <el-tab-pane label="奖励记录" name="reward">
              <div class="reward-card">
                <div class="card-title">
                  <i class="el-icon-wallet"></i>
                  <span>奖励记录{{ recordsTotal ? ' (' + recordsTotal + ')' : '' }}</span>
                  <el-button
                    v-if="recordsStatus === '0' && records.length > 0"
                    type="primary"
                    size="mini"
                    class="withdraw-btn"
                    :loading="withdrawLoading"
                    :disabled="pendingTotalMoney < 2"
                    @click="handleWithdraw"
                  >一键提现</el-button>
                </div>
                <div class="filter-bar">
                  <span class="filter-label">状态</span>
                  <el-radio-group v-model="recordsStatus" size="mini" @change="onRecordsStatusChange">
                    <el-radio-button label="">全部</el-radio-button>
                    <el-radio-button label="0">待提现</el-radio-button>
                    <el-radio-button label="1">已提现</el-radio-button>
                    <el-radio-button label="2">已驳回</el-radio-button>
                  </el-radio-group>
                </div>
                <div class="withdraw-tip" :class="{ 'is-insufficient': pendingTotalMoney < 2 }">
                  <i class="el-icon-info"></i>
                  <span v-if="pendingTotalMoney < 2">当前待提现金额 {{ pendingTotalMoney.toFixed(2) }} 元，累计满 2 元方可发起提现</span>
                  <span v-else>当前待提现金额 {{ pendingTotalMoney.toFixed(2) }} 元，可发起提现</span>
                </div>

                <div v-loading="recordsLoading" class="list-wrap">
                  <div v-if="records.length" class="record-list">
                    <div v-for="item in records" :key="item.id" class="record-card">
                      <div class="record-head">
                        <div class="record-user">
                          <div class="user-avatar" :style="{ background: avatarColor(item.nickname) }">{{ initial(item.nickname) }}</div>
                          <div class="user-meta">
                            <div class="user-name">{{ item.nickname || '匿名用户' }}</div>
                          </div>
                        </div>
                        <el-tag :type="statusType(item.status)" size="mini" effect="light">{{ statusText(item.status) }}</el-tag>
                      </div>
                      <div class="record-body">
                            <div class="record-row">
                          <span class="rl-label">奖励金额</span>
                          <span class="rl-value money">{{ formatMoney(item.money) }}</span>
                        </div>
                        <div class="record-row">
                          <span class="rl-label">备注</span>
                          <span class="rl-value">{{ item.remark || '-' }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <el-empty v-else description="暂无奖励记录" :image-size="100"></el-empty>
                </div>

                <div v-if="recordsTotal > recordsPageSize" class="pager">
                  <el-pagination
                    background
                    layout="prev, pager, next"
                    :current-page="recordsPage"
                    :page-size="recordsPageSize"
                    :total="recordsTotal"
                    small
                    @current-change="onRecordsPageChange"
                  ></el-pagination>
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>

        <!-- 绑定确认弹窗 -->
        <el-dialog
          title="确认绑定邀请码"
          :visible.sync="confirmVisible"
          width="86%"
          top="15vh"
          :close-on-click-modal="false"
          :append-to-body="true"
          custom-class="bind-confirm-dialog"
        >
          <div v-if="previewDetail" class="confirm-body">
            <div class="inviter-info">
                <img v-if="avatarUrl(previewDetail.inviter_avatar)" :src="avatarUrl(previewDetail.inviter_avatar)" class="inviter-avatar" alt="" @error="onAvatarError" />
                <div class="inviter-avatar placeholder" :style="avatarUrl(previewDetail.inviter_avatar) ? { display: 'none' } : { background: avatarColor(previewDetail.inviter_nickname) }">{{ initial(previewDetail.inviter_nickname) }}</div>
              <div class="inviter-meta">
                <div class="inviter-name">{{ previewDetail.inviter_nickname || '匿名用户' }}</div>
                <div class="inviter-sub">ID：{{ previewDetail.inviter_user_id }} · {{ sexText(previewDetail.inviter_sex) }}</div>
              </div>
            </div>
            <p class="confirm-tip">绑定后将与该邀请人建立奖励关系（好友提现 10% 奖励），且每位用户仅可绑定一次，请确认邀请码无误。</p>
          </div>
          <span slot="footer" class="dialog-footer">
            <el-button @click="confirmVisible = false" :disabled="confirmLoading">再想想</el-button>
            <el-button type="primary" :loading="confirmLoading" @click="doBind">确认绑定</el-button>
          </span>
        </el-dialog>

        <div class="bottom-bar">© 2026 陌撩漂流瓶</div>
      </template>
    </div>
  </div>
</template>

<script>
import { ActivityBindingApi, InviteBindingApi, InviteBindingPreviewApi, InviteeWithdrawRecordsApi, InviteeListApi, VerifySignatureApi, RewardWithdrawApi } from '@/api/api.js'
import { setToken } from '@/utils/dataStorage.js'
import { setAuthParams, buildAuthQuery, isAuthParamsComplete } from '@/utils/authParams.js'

export default {
  name: 'InviteActivity',
  data() {
    return {
      userId: '',
      authVerified: false,
      authVerifying: true,
      authDeniedReason: '',
      // 标签页
      activeTab: 'invite',
      canBindInvite: true,
      // 绑定
      bindCode: '',
      previewLoading: false,
      previewDetail: null,
      confirmVisible: false,
      confirmLoading: false,
      bindingLoading: false,
      bindingDetail: null,
      // 提现记录
      records: [],
      recordsTotal: 0,
      recordsPage: 1,
      recordsPageSize: 10,
      recordsStatus: '0',  // 默认显示"待提现"
      recordsLoading: false,
      recordsLoaded: false, // 是否已加载过奖励记录
      // 待提现总金额（后端返回，不受分页/状态筛选影响）
      pendingTotalMoney: 0,
      // 批量提现
      withdrawLoading: false,
      // 被邀请人
      invitees: [],
      inviteesTotal: 0,
      inviteesPage: 1,
      inviteesPageSize: 10,
      inviteesSort: 'desc',
      inviteesLoading: false,
      inviteesLoaded: false, // 是否已加载过被邀请人列表
      // 复制链接
      copySuccess: false,
    }
  },
  computed: {
    // 根据当前URL的sign参数生成邀请链接
    inviteLink() {
      const sign = (this.$route.query && this.$route.query.sign) || '';
      return sign ? `https://www.moliaos.com/#/inviteFriend?sign=${sign}` : '';
    }
  },
  mounted() {
    this.initContext();
  },
  methods: {
    goBack() {
      this.$router.push({ path: '/activity/list', query: buildAuthQuery(this.$route.query) });
    },
    // 从 URL 读取签名参数并验证身份
    initContext() {
      const query = this.$route.query || {};
      if (query.token) {
        setToken(query.token);
      }
      // 参数完整性检查：签名参数齐全才请求后端验证签名
      if (isAuthParamsComplete(query)) {
        setAuthParams(query);
        VerifySignatureApi().then(res => {
          if (res && res.code === 200 && res.data && res.data.user_id) {
            this.userId = String(res.data.user_id);
            this.authVerified = true;
            // 先加载绑定信息，根据绑定状态决定默认标签页后按需加载其他数据
            this.fetchBinding();
          } else {
            this.authVerified = false;
          }
        }).catch(err => {
          // 提取 403 业务限制原因（性别限制/黑名单等），显示友好的限制提示卡片
          const detail = err && err.response && err.response.data && err.response.data.detail;
          if (detail) {
            this.authDeniedReason = detail;
          }
          this.authVerified = false;
        }).finally(() => {
          this.authVerifying = false;
        });
      } else {
        // 参数不全，直接拒绝
        this.authVerified = false;
        this.authVerifying = false;
      }
    },
    // 查询邀请码绑定状态（data 始终非 null）
    fetchBinding() {
      this.bindingLoading = true;
      InviteBindingApi().then(res => {
        if (res && res.code === 200 && res.data) {
          // 如果有 inviter_user_id 说明已绑定
          this.bindingDetail = res.data.inviter_user_id ? res.data : null;
          this.canBindInvite = res.data.can_bind_invite !== false;
          // 根据绑定状态和绑定期限决定默认标签页
          if (this.bindingDetail || !this.canBindInvite) {
            this.activeTab = 'reward';
          }
          // 根据默认标签页按需加载数据
          this.$nextTick(() => {
            if (this.activeTab === 'invite') {
              this.fetchInvitees();
            } else {
              this.fetchRecords();
            }
          });
        } else if (res) {
          this.$message.error(res.msg || '获取绑定信息失败');
        }
      }).catch(() => {
        this.$message.error('网络异常，请稍后重试');
      }).finally(() => {
        this.bindingLoading = false;
      });
    },
    // 标签页切换：按需加载对应数据
    onTabClick(tab) {
      if (tab.name === 'invite' && !this.inviteesLoaded) {
        this.fetchInvitees();
      }
      if (tab.name === 'reward' && !this.recordsLoaded) {
        this.fetchRecords();
      }
    },
    // 点击"立即绑定"：先拉取邀请人信息做前置校验，通过则弹确认框
    handleBind() {
      const code = (this.bindCode || '').trim();
      if (!code) {
        this.$message.warning('请输入邀请码');
        return;
      }
      if (!/^\d+$/.test(code)) {
        this.$message.warning('邀请码格式不正确，请输入数字');
        return;
      }
      this.previewLoading = true;
      InviteBindingPreviewApi({ inviter_user_id: Number(code) }).then(res => {
        if (res && res.code === 200 && res.data) {
          this.previewDetail = res.data;
          if (!res.data.can_bind) {
            // 不可绑定（邀请人不存在/自邀/男性用户等）：给出原因，不弹窗
            this.$message.error(res.data.reason || '当前无法绑定');
          } else {
            // 可绑定：弹出确认弹窗
            this.confirmVisible = true;
          }
        } else if (res) {
          this.$message.error(res.msg || '获取邀请人信息失败');
        }
      }).catch(() => {
        this.$message.error('网络异常，请稍后重试');
      }).finally(() => {
        this.previewLoading = false;
      });
    },
    // 确认绑定
    doBind() {
      if (!this.previewDetail) return;
      this.confirmLoading = true;
      // 被邀请人身份(user_id)由请求拦截器从 sessionStorage 注入 URL query 签名校验，
      // body 仅传业务字段 inviter_user_id
      ActivityBindingApi({
        inviter_user_id: Number(this.previewDetail.inviter_user_id)
      }).then(() => {
        this.confirmVisible = false;
        this.$message.success('绑定成功');
        this.bindCode = '';
        this.previewDetail = null;
        // 刷新绑定详情，切换到"已绑定"展示
        this.fetchBinding();
      }).catch(err => {
        // 业务错误（如"注册时间超过3天，无法绑定邀请码"/"仅女性用户可参与邀请绑定"）：
        // 显示后端返回的 detail，弹窗保留以便用户查看后手动关闭
        const detail = err && err.response && err.response.data && err.response.data.detail;
        this.$message.error(detail || '绑定失败，请稍后重试');
      }).finally(() => {
        this.confirmLoading = false;
      });
    },
    // 奖励记录
    fetchRecords() {
      this.recordsLoading = true;
      const params = {
        page: this.recordsPage,
        page_size: this.recordsPageSize
      };
      if (this.recordsStatus !== '') {
        params.status = this.recordsStatus;
      }
      InviteeWithdrawRecordsApi(params).then(res => {
        if (res && res.code === 200) {
          this.records = res.data || [];
          this.recordsTotal = res.count || 0;
          // 保存待提现总金额（后端返回，用于一键提现门槛校验）
          this.pendingTotalMoney = Number(res.pending_total_money || 0);
          this.recordsLoaded = true;
        } else if (res) {
          this.$message.error(res.msg || '获取记录失败');
        }
      }).catch(() => {
        this.$message.error('网络异常，请稍后重试');
      }).finally(() => {
        this.recordsLoading = false;
      });
    },
    // 被邀请人名单
    fetchInvitees() {
      this.inviteesLoading = true;
      InviteeListApi({
        page: this.inviteesPage,
        page_size: this.inviteesPageSize,
        sort: this.inviteesSort
      }).then(res => {
        if (res && res.code === 200) {
          this.invitees = res.data || [];
          this.inviteesTotal = res.count || 0;
          this.inviteesLoaded = true;
        } else if (res) {
          this.$message.error(res.msg || '获取名单失败');
        }
      }).catch(() => {
        this.$message.error('网络异常，请稍后重试');
      }).finally(() => {
        this.inviteesLoading = false;
      });
    },
    // 一键提现：将当前用户所有待提现(type=2)记录批量提交
    handleWithdraw() {
      // 仅在待提现状态下显示按钮，再次校验防止意外调用
      if (this.recordsStatus !== '0' || this.records.length === 0) {
        this.$message.warning('请先选择待提现的记录');
        return;
      }
      // 前端门槛校验：待提现总金额需大于等于2元
      if (this.pendingTotalMoney < 2) {
        this.$message.warning('待提现金额需大于等于2元');
        return;
      }
      this.$confirm(
        '确认将所有待提现的奖励,全部提现？',
        '提现确认',
        {
          confirmButtonText: '确认提现',
          cancelButtonText: '再想想',
          type: 'warning',
          closeOnClickModal: false,
          customClass: 'mobile-adaptive-confirm',
        }
      ).then(() => {
        this.doWithdraw();
      }).catch(() => {});
    },
    doWithdraw() {
      this.withdrawLoading = true;
      RewardWithdrawApi().then(res => {
        if (res && res.code === 200 && res.data) {
          // 提现成功：展示格式化提示，包含记录笔数和总金额（$message 顶部居中，手机端更友好）
          const recordCount = res.data.record_count || 0;
          const amount = Number(res.data.amount || 0).toFixed(2);
          this.$message({
            message: `获取奖励成功，共获取${recordCount}笔，共${amount}元`,
            type: 'success',
            duration: 5000,
          });
        } else {
          // 业务错误：展示后端返回的错误信息
          const reason = (res && res.msg) || '提现失败，请稍后重试';
          this.$message({
            message: reason,
            type: 'error',
            duration: 5000,
          });
        }
        // 刷新记录列表
        this.fetchRecords();
      }).catch(err => {
        // 网络异常或 403/401 等 HTTP 错误
        const detail = err && err.response && err.response.data && err.response.data.detail;
        this.$message({
          message: detail || '网络异常，请稍后重试',
          type: 'error',
          duration: 5000,
        });
      }).finally(() => {
        this.withdrawLoading = false;
      });
    },
    onRecordsStatusChange() {
      this.recordsPage = 1;
      this.fetchRecords();
    },
    onRecordsPageChange(p) {
      this.recordsPage = p;
      this.fetchRecords();
    },
    onInviteesSortChange() {
      this.inviteesPage = 1;
      this.fetchInvitees();
    },
    onInviteesPageChange(p) {
      this.inviteesPage = p;
      this.fetchInvitees();
    },
    statusText(status) {
      if (status === 0) return '待提现';
      if (status === 1) return '已提现';
      return '已驳回';
    },
    statusType(status) {
      if (status === 0) return 'warning';
      if (status === 1) return 'success';
      return 'danger';
    },
    formatMoney(money) {
      return '¥' + Number(money || 0).toFixed(2);
    },
    sexText(sex) {
      if (sex === 1) return '男';
      if (sex === 2) return '女';
      return '未知';
    },
    avatarUrl(avatar) {
        const a = String(avatar || '');
        if (!a) return '';
        // 支持完整 http(s) URL 和以 / 开头的相对路径
        if (a.indexOf('http') === 0 || a.indexOf('/') === 0) return a;
        return '';
      },
      // 头像图片加载失败时，隐藏 <img> 并显示相邻的彩色首字母占位符
      onAvatarError(event) {
        const imgEl = event.target;
        const placeholder = imgEl.nextElementSibling;
        imgEl.style.display = 'none';
        if (placeholder && placeholder.classList.contains('user-avatar')) {
          placeholder.style.display = '';
          placeholder.style.background = placeholder.dataset.bg || '#a855f7';
        }
      },
      avatarColor(name) {
      const colors = ['#ff60c5', '#a855f7', '#ec4899', '#f43f5e', '#8b5cf6', '#f59e0b'];
      let hash = 0;
      const n = name || '';
      for (let i = 0; i < n.length; i++) hash = n.charCodeAt(i) + ((hash << 5) - hash);
      return colors[Math.abs(hash) % colors.length];
    },
    initial(name) {
      return (name || '匿').charAt(0);
    },
    // 复制邀请链接到剪贴板
    copyInviteLink() {
      const link = this.inviteLink;
      if (!link) {
        this.$message.warning('无法获取邀请链接，请从App内重新打开');
        return;
      }
      // 优先使用 Clipboard API，降级使用 execCommand
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(link).then(() => {
          this.onCopySuccess();
        }).catch(() => {
          this.fallbackCopy(link);
        });
      } else {
        this.fallbackCopy(link);
      }
    },
    // 剪贴板复制成功反馈
    onCopySuccess() {
      this.copySuccess = true;
      this.$message.success('邀请链接已复制');
      setTimeout(() => { this.copySuccess = false; }, 2000);
    },
    // 兼容旧浏览器的复制方案
    fallbackCopy(text) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        this.onCopySuccess();
      } catch (e) {
        this.$message.error('复制失败，请手动复制链接');
      }
      document.body.removeChild(textarea);
    }
  }
}
</script>

<style scoped>
.invite-page {
  min-height: 100vh;
  background: linear-gradient(160deg, #fff5f9 0%, #fff9ff 40%, #fdf2f8 100%);
  position: relative;
  overflow: hidden;
}

.bg-decoration {
  position: absolute;
  top: -160px;
  right: -160px;
  width: 420px;
  height: 420px;
  background: radial-gradient(circle, rgba(255, 96, 197, 0.18) 0%, transparent 70%);
  border-radius: 50%;
  animation: float 6s ease-in-out infinite;
}

.bg-decoration-2 {
  position: absolute;
  bottom: -120px;
  left: -120px;
  width: 320px;
  height: 320px;
  background: radial-gradient(circle, rgba(168, 85, 247, 0.12) 0%, transparent 70%);
  border-radius: 50%;
  animation: float 8s ease-in-out infinite reverse;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(20px, 20px) scale(1.08); }
}

.main-content {
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  padding: 24px 18px 40px;
  position: relative;
  z-index: 1;
}

/* 未授权引导 */
.no-access-card {
  background: #fff;
  border-radius: 24px;
  padding: 56px 28px;
  text-align: center;
  box-shadow: 0 12px 40px rgba(255, 96, 197, 0.12);
  margin-top: 80px;
}
.no-access-card.denied {
  border: 1px solid #fde2e2;
  background: linear-gradient(135deg, #fff5f5 0%, #fff0f5 100%);
}
.no-access-card.denied .no-access-icon {
  color: #f56c6c;
}
.no-access-icon {
  font-size: 52px;
  color: #ff60c5;
  margin-bottom: 16px;
  display: block;
}
.no-access-title {
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin-bottom: 10px;
}
.no-access-desc {
  font-size: 14px;
  color: #999;
  line-height: 1.7;
}

/* 顶部栏 */
.top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}
.back-btn {
  font-size: 14px;
  color: #ff60c5;
  font-weight: 600;
  display: flex;
  align-items: center;
  cursor: pointer;
}
.back-btn i {
  margin-right: 4px;
}
.brand-name {
  font-size: 14px;
  font-weight: 700;
  background: linear-gradient(135deg, #ff60c5 0%, #a855f7 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Hero */
.hero-card {
  background: linear-gradient(135deg, #ff60c5 0%, #a855f7 100%);
  border-radius: 26px;
  padding: 28px 24px 24px;
  color: #fff;
  text-align: center;
  box-shadow: 0 16px 44px rgba(255, 96, 197, 0.35);
  position: relative;
  overflow: hidden;
  margin-bottom: 16px;
}
.hero-card::after {
  content: '';
  position: absolute;
  top: -40px;
  right: -40px;
  width: 160px;
  height: 160px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.18) 0%, transparent 70%);
  border-radius: 50%;
}
.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.22);
  padding: 5px 14px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 14px;
}
.hero-badge .dot {
  width: 6px;
  height: 6px;
  background: #fff;
  border-radius: 50%;
  animation: blink 1.4s ease-in-out infinite;
}
@keyframes blink {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}
.hero-title {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 6px;
  letter-spacing: 1px;
}
.hero-cashback {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
}
.hero-cashback .num {
  font-size: 56px;
  font-weight: 800;
  line-height: 1;
  vertical-align: -8px;
  margin: 0 2px;
  text-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}
.hero-cashback .pct {
  font-size: 28px;
  font-weight: 800;
}
.hero-desc {
  font-size: 13px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.92);
  margin-bottom: 20px;
}
.hero-desc b {
  font-weight: 700;
}
.hero-stats {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.16);
  border-radius: 16px;
  padding: 14px 0;
}
.stat-item {
  flex: 1;
  text-align: center;
}
.stat-num {
  font-size: 22px;
  font-weight: 800;
  margin-bottom: 4px;
}
.stat-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
}
.stat-divider {
  width: 1px;
  height: 30px;
  background: rgba(255, 255, 255, 0.3);
}

/* Tabs 容器 */
.tabs-card {
  background: #fff;
  border-radius: 22px;
  padding: 4px 18px 20px;
  box-shadow: 0 8px 28px rgba(255, 96, 197, 0.1);
  margin-bottom: 16px;
  border: 1px solid rgba(255, 96, 197, 0.06);
}
/* Tabs 导航样式 */
.invite-tabs >>> .el-tabs__item {
  font-size: 15px;
  font-weight: 600;
  color: #888;
}
.invite-tabs >>> .el-tabs__item.is-active {
  color: #ff60c5;
}
.invite-tabs >>> .el-tabs__active-bar {
  background-color: #ff60c5;
}
.invite-tabs >>> .el-tabs__nav-wrap::after {
  height: 1px;
  background-color: #f0d6e6;
}
/* 标签页切换动画 */
.invite-tabs >>> .el-tab-pane {
  animation: tabFadeIn 0.25s ease-out;
}
@keyframes tabFadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* 标签页内卡片（去掉独立圆角/阴影/边框） */
.bind-card,
.reward-card {
  padding: 16px 0;
  margin-bottom: 0;
}
.bind-card + .bind-card {
  border-top: 1px dashed #f0d6e6;
}
.card-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin-bottom: 14px;
}
.card-title i {
  color: #ff60c5;
  margin-right: 8px;
  font-size: 18px;
}
/* 提现按钮（内嵌在 card-title 右侧） */
.withdraw-btn {
  margin-left: auto;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 14px;
  background: linear-gradient(135deg, #ff60c5 0%, #a855f7 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(255, 96, 197, 0.3);
}
.withdraw-btn:hover {
  opacity: 0.9;
}
/* 提现门槛提示条 */
.withdraw-tip {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #67c23a;
  background: #f0f9eb;
  border-radius: 6px;
  padding: 6px 10px;
  margin-bottom: 12px;
}
.withdraw-tip i {
  margin-right: 5px;
  font-size: 13px;
}
.withdraw-tip.is-insufficient {
  color: #e6a23c;
  background: #fdf6ec;
}
/* 卡片内分区标题（带顶部分隔） */
.card-section-title {
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 700;
  color: #333;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px dashed #f0d6e6;
  margin-bottom: 12px;
}
.card-section-title.no-border {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}
.card-section-title i {
  color: #ff60c5;
  margin-right: 8px;
  font-size: 16px;
}
/* 被邀请人排序控件（内嵌在分区标题右侧） */
.invitees-sort-wrap {
  margin-left: auto;
}
.invitees-sort-wrap >>> .el-radio-button__inner {
  padding: 4px 8px;
  border-color: #f0d6e6;
  color: #666;
  font-size: 11px;
}
.invitees-sort-wrap >>> .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  background: linear-gradient(135deg, #ff60c5 0%, #a855f7 100%);
  border-color: #ff60c5;
  color: #fff;
  box-shadow: -1px 0 0 0 #ff60c5;
}
/* 复制邀请链接栏 */
.copy-link-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #fff5fb 0%, #f5f0ff 100%);
  border: 1px solid #f0d6e6;
  border-radius: 14px;
}
.link-preview {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: #999;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: Consolas, Monaco, monospace;
}
.copy-link-btn {
  flex-shrink: 0;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 12px;
  border-color: #f0d6e6;
  color: #ff60c5;
  transition: all 0.25s ease;
}
.copy-link-btn:hover {
  color: #fff;
  background: linear-gradient(135deg, #ff60c5 0%, #a855f7 100%);
  border-color: #ff60c5;
}
.copy-link-btn.copy-success {
  color: #fff;
  background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%);
  border-color: #a855f7;
}
.rules-list {
  margin: 0;
  padding-left: 18px;
  color: #666;
  font-size: 13px;
  line-height: 1.9;
}
.rules-list li {
  margin-bottom: 4px;
}
.rules-list b {
  color: #ff60c5;
}

/* 邀请码绑定卡 */
.bind-status-tag {
  margin-left: auto;
  background: linear-gradient(135deg, #ff60c5 0%, #a855f7 100%);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 10px;
}
.bind-body {
  min-height: 120px;
}
.form-label {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}
.bind-card >>> .el-input__inner {
  border-radius: 14px;
  border-color: #f0d6e6;
  height: 48px;
  font-size: 16px;
}
.bind-card >>> .el-input__inner:focus {
  border-color: #ff60c5;
}
.submit-btn {
  width: 100%;
  margin-top: 16px;
  height: 48px;
  border-radius: 24px;
  font-size: 16px;
  font-weight: 600;
  background: linear-gradient(135deg, #ff60c5 0%, #a855f7 100%);
  border: none;
  box-shadow: 0 8px 20px rgba(255, 96, 197, 0.35);
}
.submit-btn:disabled {
  opacity: 0.6;
}
.bind-tip {
  margin-top: 14px;
  font-size: 12px;
  color: #aaa;
  text-align: center;
}

/* 超过绑定期限提示 */
.bind-expired-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 14px;
  background: #fff5f5;
  border: 1px solid #fde2e2;
  border-radius: 12px;
  color: #f56c6c;
  font-size: 13px;
  margin-bottom: 14px;
  line-height: 1.6;
}
.bind-expired-notice i {
  font-size: 16px;
  flex-shrink: 0;
}

/* 已绑定信息 - 头像卡片式布局 */
.bound-info {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #fff5fb 0%, #f5f0ff 100%);
  border: 1px solid #f0d6e6;
  border-radius: 14px;
  padding: 14px 16px;
}
.bound-avatar-wrap {
  flex-shrink: 0;
  margin-right: 14px;
}
.bound-avatar-img {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}
.bound-avatar-placeholder {
  width: 52px;
  height: 52px;
  border-radius: 50% !important;
  font-size: 22px;
}
.bound-detail {
  flex: 1;
  min-width: 0;
}
.bound-name {
  font-size: 15px;
  font-weight: 700;
  color: #333;
  margin-bottom: 4px;
}
.bound-sex-tag {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 8px;
  margin-left: 6px;
  vertical-align: middle;
}
.bound-sex-tag.female {
  background: #fce7f3;
  color: #ec4899;
}
.bound-sex-tag.male {
  background: #ede9fe;
  color: #8b5cf6;
}
.bound-id {
  font-size: 12px;
  color: #999;
  margin-bottom: 2px;
}
.bound-time {
  font-size: 12px;
  color: #bbb;
}

/* 绑定确认弹窗 */
>>> .bind-confirm-dialog {
  border-radius: 20px;
  overflow: hidden;
}
>>> .bind-confirm-dialog .el-dialog__header {
  padding: 18px 20px 12px;
}
>>> .bind-confirm-dialog .el-dialog__title {
  font-size: 16px;
  font-weight: 700;
  color: #333;
}
>>> .bind-confirm-dialog .el-dialog__body {
  padding: 6px 20px 18px;
  /* 内容超出时可滚动，避免小屏内容被截断 */
  max-height: 65vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
>>> .bind-confirm-dialog .el-dialog__footer {
  padding: 0 20px 20px;
}
.confirm-body {
  text-align: center;
}
.inviter-info {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}
.inviter-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 12px;
  flex-shrink: 0;
}
.inviter-avatar.placeholder {
  color: #fff;
  font-size: 22px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.inviter-meta {
  text-align: left;
}
.inviter-name {
  font-size: 16px;
  font-weight: 700;
  color: #333;
}
.inviter-sub {
  font-size: 12px;
  color: #999;
  margin-top: 3px;
}
.confirm-tip {
  font-size: 13px;
  color: #888;
  line-height: 1.7;
  margin: 0;
}
.dialog-footer {
  display: flex;
  justify-content: space-between;
}
.dialog-footer .el-button {
  flex: 1;
  margin: 0 6px;
  border-radius: 22px;
  height: 42px;
}
.dialog-footer .el-button--primary {
  background: linear-gradient(135deg, #ff60c5 0%, #a855f7 100%);
  border: none;
  box-shadow: 0 6px 16px rgba(255, 96, 197, 0.3);
}

/* 筛选条 */
.filter-bar {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
}
.filter-label {
  font-size: 13px;
  color: #888;
  margin-right: 12px;
}
.filter-bar >>> .el-radio-button__inner {
  border-color: #f0d6e6;
  color: #666;
}
.filter-bar >>> .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  background: linear-gradient(135deg, #ff60c5 0%, #a855f7 100%);
  border-color: #ff60c5;
  color: #fff;
  box-shadow: -1px 0 0 0 #ff60c5;
}

/* 列表 */
.list-wrap {
  min-height: 120px;
}
.record-card,
.invitee-card {
  background: #fff8fc;
  border: 1px solid #f5e0ee;
  border-radius: 16px;
  padding: 14px 14px 12px;
  margin-bottom: 12px;
}
.record-head,
.invitee-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.record-user,
.invitee-user {
  display: flex;
  align-items: center;
}
.user-avatar {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
}
/* 头像图片：与 .user-avatar 同尺寸，加载失败时由 onAvatarError 隐藏 */
.user-avatar-img {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  object-fit: cover;
  margin-right: 10px;
  flex-shrink: 0;
  background: #f5eef8;
}
.user-meta,
.invitee-meta {
  display: flex;
  flex-direction: column;
}
.user-name,
.invitee-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}
.invitee-id,
.user-phone,
.invitee-phone {
  font-size: 12px;
  color: #aaa;
  margin-top: 2px;
}
.invitee-time {
  font-size: 12px;
  color: #bbb;
  text-align: right;
}
/* 黑名单被邀请人标识 */
.blacklist-tag {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  color: #f56c6c;
  background: #fef0f0;
  border: 1px solid #fde2e2;
  padding: 0 6px;
  border-radius: 6px;
  margin-left: 6px;
  vertical-align: middle;
  line-height: 18px;
}
.invitee-blacklisted {
  background: #fff5f5;
  border-color: #fde2e2;
}
.invitee-blacklisted .invitee-name {
  color: #f56c6c;
}
.record-body {
  border-top: 1px dashed #f0d6e6;
  padding-top: 10px;
}
.record-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  padding: 3px 0;
}
.rl-label {
  color: #999;
}
.rl-value {
  color: #555;
}
.rl-value.money {
  color: #ff60c5;
  font-weight: 700;
  font-size: 15px;
}
.rl-value.money .coin {
  font-style: normal;
  font-size: 11px;
  color: #c0a0b8;
  font-weight: 400;
}
.mono {
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
}
.invitee-foot {
  border-top: 1px dashed #f0d6e6;
  padding-top: 10px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 12px;
  gap: 8px;
}
.rel-label {
  color: #999;
}
.rel-value {
  color: #ff60c5;
  font-weight: 600;
}
.rel-id {
  color: #bbb;
  margin-left: auto;
}

/* 点击刷新数据提示 */
.load-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 140px;
  color: #ff60c5;
  font-size: 13px;
  cursor: pointer;
  border-radius: 14px;
  transition: background 0.2s ease;
}
.load-prompt:hover {
  background: #fff5fb;
}
.load-prompt i {
  font-size: 28px;
  color: #ff60c5;
}

.pager {
  margin-top: 18px;
  text-align: center;
}
.pager >>> .el-pagination.is-background .el-pager li:not(.disabled).active {
  background-color: #ff60c5;
}
.pager >>> .el-pagination.is-background .btn-prev,
.pager >>> .el-pagination.is-background .btn-next,
.pager >>> .el-pagination.is-background .el-pager li {
  background-color: #fff;
}

.bottom-bar {
  text-align: center;
  font-size: 12px;
  color: #ccc;
  margin-top: 24px;
}

/* 响应式 - 大屏 */
@media (min-width: 481px) {
  .main-content {
    padding: 40px 18px 48px;
  }
  .tabs-card {
    padding: 8px 22px 24px;
  }
}

/* 响应式 - 小屏 */
@media (max-width: 360px) {
  .hero-cashback .num {
    font-size: 46px;
  }
  .hero-cashback .pct {
    font-size: 24px;
  }
  .hero-title {
    font-size: 20px;
  }
  .record-row,
  .invitee-foot {
    font-size: 12px;
  }
  /* 小屏头像缩小 */
  .bound-avatar-img,
  .bound-avatar-placeholder {
    width: 42px !important;
    height: 42px !important;
    font-size: 18px !important;
  }
  .bound-name {
    font-size: 13px;
  }
  /* 标签页字号缩小 */
  .invite-tabs >>> .el-tabs__item {
    font-size: 13px;
  }
}
</style>

<!-- 全局样式：覆盖挂载到 body 的 Element UI 组件（$confirm/$message/$notify），与页面粉紫色系统一 -->
<style>
/* MessageBox 确认框样式统一 */
.el-message-box {
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid #f0d6e6;
  box-shadow: 0 12px 40px rgba(255, 96, 197, 0.18);
  /* 手机端自适应：宽度按视口百分比，避免 420px 默认宽度在窄屏溢出 */
  width: auto !important;
  max-width: 86% !important;
}
/* 自适应确认框：内容超出时可纵向滚动 */
.mobile-adaptive-confirm .el-message-box__content {
  max-height: 60vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  word-break: break-word;
}
.el-message-box .el-message-box__header {
  padding: 18px 20px 10px;
}
.el-message-box .el-message-box__title {
  font-size: 16px;
  font-weight: 700;
  color: #333;
}
.el-message-box .el-message-box__content {
  padding: 10px 20px 18px;
  font-size: 14px;
  color: #666;
}
.el-message-box .el-message-box__btns {
  padding: 0 20px 20px;
}
.el-message-box .el-button--primary {
  background: linear-gradient(135deg, #ff60c5 0%, #a855f7 100%);
  border: none;
  border-radius: 20px;
  box-shadow: 0 4px 12px rgba(255, 96, 197, 0.3);
  font-weight: 600;
}
.el-message-box .el-button--primary:hover {
  opacity: 0.9;
}
.el-message-box .el-button--default {
  border-radius: 20px;
  border-color: #f0d6e6;
  color: #666;
}
.el-message-box .el-button--default:hover {
  color: #ff60c5;
  border-color: #ff60c5;
  background: #fff5fb;
}

/* Message 提示样式统一 */
.el-message {
  border-radius: 14px;
  border-color: #f0d6e6;
  box-shadow: 0 8px 24px rgba(255, 96, 197, 0.14);
  min-width: 260px;
}
.el-message--success .el-message__icon {
  color: #a855f7;
}
.el-message--warning .el-message__icon {
  color: #f59e0b;
}
.el-message--error .el-message__icon {
  color: #f43f5e;
}
.el-message .el-message__content {
  font-size: 14px;
  color: #333;
}

/* Notification 通知样式统一 */
.el-notification {
  border-radius: 18px;
  border: 1px solid #f0d6e6;
  box-shadow: 0 10px 32px rgba(255, 96, 197, 0.16);
  overflow: hidden;
}
.el-notification .el-notification__title {
  font-size: 15px;
  font-weight: 700;
  color: #333;
}
.el-notification .el-notification__content {
  font-size: 13px;
  color: #666;
  line-height: 1.6;
}
.el-notification--success {
  border-left: 4px solid #a855f7;
}
.el-notification--success .el-notification__title {
  color: #a855f7;
}
.el-notification--error {
  border-left: 4px solid #f43f5e;
}
.el-notification--error .el-notification__title {
  color: #f43f5e;
}
.el-notification .el-notification__closeBtn {
  color: #c0a0b8;
}
.el-notification .el-notification__closeBtn:hover {
  color: #ff60c5;
}
</style>
